import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { NoData } from "src/shared/components/tables/no-data";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack, TablePagination, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { MenuPermissions } from "src/constants/permissions";
import { downloadAsXLS, oneBranchHasPermission } from "src/utils";
import {
  useDownloadIMenuProducts,
  useDownloadIMenuRecipes,
  useGetMenuModifiers,
} from "src/api/inventory/menu";
import AddIcon from "@mui/icons-material/Add";
import AddEditModifierPopup from "./add-edit-modifier";
import TableActions from "./table-actions";
import ImportMenuProductsPopup from "../../import-menu";
import ImportMenuProductsRecipesPopup from "../../import-menu-recipe";
import { LoadingButton } from "@mui/lab";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import FilterDrawer from "../all/filters";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";

const ModifiersList = () => {
  const { t } = useTranslation("purchasing");
  const { t: inventoryT } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const [open, setOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [openImport, setOpenImport] = useState(false);
  const [openImportRecipe, setOpenImportRecipe] = useState(false);
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [type, setType] = useState("all");
  const [openFilter, setOpenFilter] = useState(false);

  const HeadText = [
    t("name"),
    inventoryT("sku"),
    inventoryT("costCenter"),
    t("costMethod"),
    inventoryT("cost"),
    `${inventoryT("cost")} %`,
    inventoryT("price"),
    generalT("createdBy"),
    generalT("createdAt"),
    generalT("actions"),
  ];

  const filters = {
    type: "modifier",
    ...(!!name && { name: name }),
    ...(!!sku && { sku: sku }),
  };

  // APIS
  const { data, isFetching, refetch } = useGetMenuModifiers({
    page: pageNumber + 1,
    ...filters,
  });
  const {
    data: downloadedData,
    refetch: refetchDownload,
    isFetching: isFetchingDownload,
    status,
  } = useDownloadIMenuProducts(filters);
  const {
    data: downloadedRecipeData,
    refetch: refetchRecipeDownload,
    isFetching: isFetchingRecipeDownload,
    status: recipeStatus,
  } = useDownloadIMenuRecipes(filters);

  // permissions
  const addModifierPer = oneBranchHasPermission(MenuPermissions.addModifier);
  const addModifierRecipePer = oneBranchHasPermission(
    MenuPermissions.AddModifierRecipe
  );

  const emptyDataArr = data?.data.length === 0;

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = (type: any) => {
    setOpen(true);
  };

  const handleOpenImport = () => setOpenImport(true);
  const handleCloseImport = () => setOpenImport(false);

  const handleOpenImportRecipe = () => setOpenImportRecipe(true);
  const handleCloseImportRecipe = () => setOpenImportRecipe(false);

  const handleOpenFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage);
  };

  const handleDownload = () => {
    refetchDownload();
  };

  const handleDownloadRecipe = () => {
    refetchRecipeDownload();
  };

  useEffect(() => {
    if (!!data) {
      setTotal(data.meta?.total as number);
    }
  }, [data]);

  useEffect(() => {
    if (status === "success" && !!downloadedData) {
      const utf8CSVData = `\uFEFF${downloadedData}`;
      downloadAsXLS(utf8CSVData, "modifiers");
    }
  }, [isFetchingDownload]);

  useEffect(() => {
    if (recipeStatus === "success" && !!downloadedRecipeData) {
      const utf8CSVData = `\uFEFF${downloadedRecipeData}`;
      downloadAsXLS(utf8CSVData, "modifiers-recipe");
    }
  }, [isFetchingRecipeDownload]);

  return (
    <Stack spacing={3} maxWidth={{ xs: "100%", md: `calc(100vw - 316px)` }}>
      <Box display={"flex"} justifyContent={"flex-end"}>
        <Stack
          direction={"row"}
          gap={2}
          flexWrap={"wrap"}
          mt={{ xs: 2, md: 6 }}
        >
          <Button
            color="tertiary"
            variant="outlined"
            endIcon={<img src={FilterIcon} height={"16px"} alt="" />}
            onClick={handleOpenFilter}
          >
            {t("filter")}
          </Button>
          <LoadingButton
            sx={{
              border: "1px solid #98A2B3",
              height: "40px",
              fontWeight: 500,
              borderRadius: "4px",
              padding: "7px 10px",
              background: "#FCFCFD",
              fontSize: "16px",
            }}
            color="tertiary"
            variant="outlined"
            endIcon={<img src={DownloadIcon} alt="" />}
            loading={isFetchingDownload}
            onClick={handleDownload}
          >
            {t("download")}
          </LoadingButton>
          {addModifierRecipePer && (
            <LoadingButton
              sx={{
                border: "1px solid #98A2B3",
                height: "40px",
                fontWeight: 500,
                borderRadius: "4px",
                padding: "7px 10px",
                background: "#FCFCFD",
                fontSize: "16px",
              }}
              color="tertiary"
              variant="outlined"
              endIcon={<img src={DownloadIcon} alt="" />}
              loading={isFetchingRecipeDownload}
              onClick={handleDownloadRecipe}
            >
              {inventoryT("downloadRecipe")}
            </LoadingButton>
          )}
          {addModifierPer && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenImport}
            >
              {inventoryT("importMenuModifiers")}
            </Button>
          )}
          {addModifierRecipePer && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenImportRecipe}
            >
              {inventoryT("importRecipe")}
            </Button>
          )}
          {addModifierPer && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpen}
            >
              {inventoryT("createModifier")}
            </Button>
          )}
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {HeadText.map((item) => (
                <TableCell
                  key={item}
                  align={"center"}
                  sx={{
                    bgcolor: "#F9FAFB",
                  }}
                >
                  <Typography
                    fontSize={"12px"}
                    color={"#475467"}
                    lineHeight={"18px"}
                    fontWeight={500}
                  >
                    {item}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isFetching || !data ? (
            <TableLoadingSkeleton rowsLength={15} cellsLength={10} />
          ) : (
            <TableBody>
              {data.data.map((row: any) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align="center">{row.name}</TableCell>
                  <TableCell align="center">{row.sku}</TableCell>
                  <TableCell align="center">
                    {row.menu_recipe_id && !row.branch
                      ? inventoryT("dynamic")
                      : row.branch?.name ?? "--"}
                  </TableCell>
                  <TableCell align="center">
                    {row.method === "recipe"
                      ? inventoryT("recipeCost")
                      : t("fixedCost")}
                  </TableCell>
                  <TableCell align="center">
                    {row.unit_cost.toFixed(3)}
                  </TableCell>
                  <TableCell align="center">
                    {((row.unit_cost / row.price) * 100).toFixed(2)} %
                  </TableCell>
                  <TableCell align="center">{row.price}</TableCell>
                  <TableCell align="center">{row.created_by?.name}</TableCell>
                  <TableCell align="center">{row.created_at}</TableCell>
                  <TableCell align="right">
                    <TableActions row={row} refetch={refetch} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {emptyDataArr && <NoData />}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[20]}
        component="div"
        count={total}
        rowsPerPage={20}
        page={pageNumber}
        onPageChange={handleChangePage}
        showFirstButton
        showLastButton
      />

      <FilterDrawer
        open={openFilter}
        name={name}
        setName={setName}
        sku={sku}
        setSku={setSku}
        type={type}
        setType={setType}
        handleClose={handleCloseFilter}
      />

      {open && (
        <AddEditModifierPopup
          open={open}
          handleClose={handleClose}
          refetch={refetch}
        />
      )}

      {openImport && (
        <ImportMenuProductsPopup
          open={openImport}
          handleClose={handleCloseImport}
          refetch={refetch}
          type="modifier"
        />
      )}

      {openImportRecipe && (
        <ImportMenuProductsRecipesPopup
          open={openImportRecipe}
          handleClose={handleCloseImportRecipe}
          refetch={refetch}
          type="modifier"
        />
      )}
    </Stack>
  );
};
export default ModifiersList;
